import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Toggle, Visibility } from '@reservamos/elements';
import SeatSelectorComponent from 'components/purchase/SeatSelector';
import usePurchase from 'hooks/store/usePurchase';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { getSeatPreferenceDiagramType, getSeatSelectorPropsFromPurchase } from 'utils/seats';
import {
  getConnectionTransferRouteLabel,
  replicateSeatsAcrossConnectionLegs,
} from 'utils/purchase/connectionSameSeat';
import saveConfirmedSeatPreference from 'utils/purchase/seatSelectionConfirmation';

/**
 * Returns a string with the trip route label
 * this label is composed by the origin and destination names
 * and it looks like this: "Origin - Destination"
 *
 * @param {*} trip
 * @returns the trip route label
 *
 * @example
 *
 * ```js
 *   const firstTrip = trips[0];
 *   const firstTripRouteLabel = getTripRouteLabel(firstTrip);
 *   console.log(firstTripRouteLabel); // "Belem - Florianopolis"
 * ```
 */
function getTripCitiesLabel(trip) {
  const tripOriginName = trip.terminals[trip.trip.originId].cityName;
  const tripDestinationName = trip.terminals[trip.trip.destinationId].cityName;
  const tripCitiesLabel = `${tripOriginName} - ${tripDestinationName}`;
  return tripCitiesLabel;
}

const propTypes = {
  way: PropTypes.oneOf(['departure', 'return']).isRequired,
  selectSeats: PropTypes.func.isRequired,
  finishSeatSelection: PropTypes.func.isRequired,
};

/**
 * Renders seat selector for trips with connections.
 * It supports only one connection (two legs trip).
 *
 * When CONNECTION_SAME_SEAT is enabled, shows a single map and replicates the
 * selected seat onto every connection leg (legs are informational only).
 *
 * @todo
 * - Add support for more than one connection (when needed).
 */
const MultiTripSeatSelector = ({ way, selectSeats, finishSeatSelection }) => {
  const purchase = usePurchase();
  const features = useWhitelabelFeatures();
  const connectionSameSeat = Boolean(features?.CONNECTION_SAME_SEAT);
  const { t } = useTranslation('seats');
  const {
    departs: {
      layouts: departureLayouts = {},
      seats: departureSeats = {},
      trips: departureTripsState = [],
      tickets: departureTickets = [],
    },
    returns: {
      layouts: returnLayouts = {},
      seats: returnSeats = {},
      trips: returnTripsState = [],
      tickets: returnTickets = [],
    },
  } = purchase;
  const seats = way === 'departure' ? departureSeats : returnSeats;
  const layouts = way === 'departure' ? departureLayouts : returnLayouts;
  const lockedSeats = way === 'departure' ? departureTickets : returnTickets;
  const tripsState = way === 'departure' ? departureTripsState : returnTripsState;
  const trips = tripsState.map((trip) => ({ ...trip, id: trip.trip.id }));
  const [currentTrip, setCurrentTrip] = useState(trips[0]);
  const {
    sendNotification,
    updateSeat,
    isExchange,
    busCategories,
    isLoading,
    allowsSeatSelection,
    passengers,
    diagramType,
    isLockingTickets = false,
    fetchingDetails = true,
    selectedSeats = [],
    isOpenTicket,
    canGoBack,
    tripPricing,
    hasSeats,
    resultsLayout,
    onClose,
    clearError,
    fetchingDetailsError,
    minPassengers,
    bookingPassengerCount,
  } = getSeatSelectorPropsFromPurchase({ way, purchase });
  const loading = !allowsSeatSelection || isLockingTickets || !hasSeats || isLoading;

  /**
   * Replaces selected seats for one connection leg.
   * @param {string} tripSlug - Trip slug of the leg being edited.
   * @returns {Function} Handler that receives the new seats for that leg.
   */
  const onSelectTripSeats = (tripSlug) => (tripSeats) => {
    const newSelectedSeats = selectedSeats.filter((seat) => seat.tripSlug !== tripSlug);
    newSelectedSeats.push(...tripSeats);
    selectSeats(newSelectedSeats);
  };

  /**
   * Replicates first-map seats onto every connection leg tripSlug.
   * @param {Array<object>} tripSeats - Seats selected on the single map.
   */
  const onSelectSameSeatAcrossLegs = (tripSeats) => {
    selectSeats(replicateSeatsAcrossConnectionLegs(tripSeats, trips));
  };

  const firstTrip = trips[0];
  const secondTrip = trips[1];
  const firstTripSelectedSeats = selectedSeats.filter((seat) => seat.tripSlug === firstTrip.id);
  const secondTripSelectedSeats = selectedSeats.filter((seat) => seat.tripSlug === secondTrip.id);
  const firstTripHasSelectedSeats = firstTripSelectedSeats.length > 0;
  const secondTripHasSelectedSeats = secondTripSelectedSeats.length > 0;
  const firstTripCitiesLabel = getTripCitiesLabel(firstTrip);
  const secondTripCitiesLabel = getTripCitiesLabel(secondTrip);
  const transferRouteLabel = getConnectionTransferRouteLabel(trips);

  const saveTripPreference = (trip) =>
    saveConfirmedSeatPreference({
      layout: layouts[trip.id],
      selectedSeats: selectedSeats.filter((seat) => seat.tripSlug === trip.id),
      passengerCount: bookingPassengerCount,
      tripSlug: trip.id,
      diagramType: getSeatPreferenceDiagramType(trip.diagramType || trip.trip?.diagramType),
      showUniformLayout: Boolean(features.SHOW_UNIFORM_LAYOUT),
    });

  const advanceToSecondTrip = () => {
    if (!firstTripHasSelectedSeats) return;
    const invalidSecondTripSeatsLength =
      secondTripSelectedSeats.length > firstTripSelectedSeats.length;
    if (invalidSecondTripSeatsLength) {
      onSelectTripSeats(secondTrip.id)(
        secondTripSelectedSeats.slice(0, firstTripSelectedSeats.length),
      );
    }
    setCurrentTrip(secondTrip);
  };

  /**
   *
   * Handles finish seat selection for the current trip leg.
   * It takes in mind only two legs trips.
   *
   * - For the firt trip:
   *  - Makes sure user has selected seats.
   *  - Removes the extra selected seats from the second trip, if any.
   *     (Second trip cannot have more selected seats than the first trip).
   *  - Sets the second trip as the current trip.
   * - For the second trip:
   *  - Makes sure user has selected seats.
   *  - Finishes the seat selection process.
   *
   * @param {H} isFirstTrip
   *
   */
  const onFinishSeatSelection =
    (isFirstTrip = false) =>
    () => {
      if (connectionSameSeat) {
        // SeatSelector finishes without seats when the diagram is unavailable.
        if (!allowsSeatSelection || firstTripHasSelectedSeats) {
          finishSeatSelection();
        }
        return;
      }

      if (isFirstTrip && firstTripHasSelectedSeats) {
        advanceToSecondTrip();
        return;
      }
      if (firstTripHasSelectedSeats && secondTripHasSelectedSeats) {
        finishSeatSelection();
      }
    };

  /**
   * Handles toggle trip leg selection for the toggle component.
   *
   * Supports only two legs trips. Handles toggle differently depending on what leg is active.
   *
   * - If the first trip is active, it delegates
   *   to onFinishSeatSelection in order to apply its verification logic and set the second trip as the current trip.
   * - If the second trip is active, it sets the first trip as the current trip.
   */
  const onToggleCurrentTrip = () => {
    const firstTripIsActive = currentTrip.id === trips[0].id;
    if (firstTripIsActive) {
      saveTripPreference(firstTrip);
      advanceToSecondTrip();
      return;
    }
    setCurrentTrip(trips[0]);
  };

  const firstTripIsActive = currentTrip.id === trips[0].id;
  const secondTripIsActive = currentTrip.id === trips[1].id;

  if (connectionSameSeat) {
    const originName = firstTrip.terminals[firstTrip.trip.originId].name;
    const destinationName = firstTrip.terminals[firstTrip.trip.destinationId].name;
    const routeLabel = `${originName} - ${destinationName}`;
    const selectedTripSeats = selectedSeats.filter((seat) => seat.tripSlug === firstTrip.id);
    const tripLockedSeats = lockedSeats.filter((seat) => seat.tripSlug === firstTrip.id);

    return (
      <Spacing vertical size="M">
        <div data-testid="connection-transfer-label">
          <Text size="S" weight="bold" color="grayStrong">
            {transferRouteLabel}
          </Text>
        </div>
        <SeatSelectorComponent
          isMultiTrip
          firstTripIsActive
          secondTripIsActive={false}
          routeLabel={routeLabel}
          headerLegend={t('your_seats')}
          key={firstTrip.id}
          way={way}
          tripId={firstTrip.id}
          layout={layouts[firstTrip.id]}
          seats={seats[firstTrip.id]}
          trip={firstTrip}
          allowsSeatSelection={allowsSeatSelection}
          diagramType={diagramType}
          fetchingDetails={fetchingDetails}
          isPetFriendly={false}
          lockedSeats={tripLockedSeats}
          selectedSeats={selectedTripSeats}
          selectSeats={onSelectSameSeatAcrossLegs}
          onFinishedSelection={onFinishSeatSelection(true)}
          sendNotification={sendNotification}
          isOpenTicket={isOpenTicket}
          isExchange={isExchange}
          passengers={isExchange && passengers}
          busCategories={busCategories}
          minPassengers={minPassengers}
          updateSeat={updateSeat}
          isLoading={loading}
          canGoBack={canGoBack}
          tripPricing={tripPricing}
          resultsLayout={resultsLayout}
          onClose={onClose}
          hasSeats={hasSeats}
          clearError={clearError}
          fetchingDetailsError={fetchingDetailsError}
          bookingPassengerCount={bookingPassengerCount}
          seatPreferenceDiagramType={getSeatPreferenceDiagramType(
            firstTrip.diagramType || firstTrip.trip?.diagramType,
          )}
        />
      </Spacing>
    );
  }

  return (
    <Spacing vertical size="M">
      <Toggle
        isChecked={secondTripIsActive}
        onClick={onToggleCurrentTrip}
        fullWidth
        alphaBg="S"
        isGradient={false}
        firstContent={
          <Spacing size="S" isResponsive>
            <Text size="S" weight="bold" color={firstTripIsActive ? 'grayStrong' : 'grayLight'}>
              {t('first_connection')}
            </Text>
            <Visibility type="hideForMedium">
              <Text color={firstTripIsActive ? 'grayStrong' : 'grayLight'} size="S">
                {firstTripCitiesLabel}
              </Text>
            </Visibility>
          </Spacing>
        }
        secondContent={
          <Spacing size="S" isResponsive>
            <Text size="S" weight="bold" color={secondTripIsActive ? 'grayStrong' : 'grayLight'}>
              {t('second_connection')}
            </Text>
            <Visibility type="hideForMedium">
              <Text color={secondTripIsActive ? 'grayStrong' : 'grayLight'} size="S">
                {secondTripCitiesLabel}
              </Text>
            </Visibility>
          </Spacing>
        }
      />
      {trips.map((trip, index) => {
        const selectedTripSeats = selectedSeats.filter((seat) => seat.tripSlug === trip.id);
        const tripLockedSeats = lockedSeats.filter((seat) => seat.tripSlug === trip.id);
        const tripIsActive = currentTrip.id === trip.id;
        const isFirstTrip = index === 0;
        const originName = trip.terminals[trip.trip.originId].name;
        const destinationName = trip.terminals[trip.trip.destinationId].name;
        const routeLabel = `${originName} - ${destinationName}`;

        /**
         * minPassengers is the minimum number of seats that the user must select for the trip.
         * getSeatSelectorPropsFromPurchase returns this value based on the way (departure or return),
         * but it only considers trips without connections. (one leg trips only)
         *
         * For trips with connections, the value of minPassengers works for:
         *  - The departure first leg. (first trip)
         *  - The return trip (for both legs)
         *
         * So, for the departure second leg, we need to calculate the minPassengers value based on the first leg selected seats.
         * this value is stored in departureTripSecondLegMinPassengers.
         */
        let tripMinPassengers = minPassengers;
        const isDepartureTripSecondLeg = way === 'departure' && !isFirstTrip;
        if (isDepartureTripSecondLeg) {
          const departureTripSecondLegMinPassengers = firstTripSelectedSeats.length;
          tripMinPassengers = departureTripSecondLegMinPassengers;
        }

        return tripIsActive ? (
          <SeatSelectorComponent
            isMultiTrip
            firstTripIsActive={isFirstTrip && tripIsActive}
            secondTripIsActive={!isFirstTrip && tripIsActive}
            routeLabel={routeLabel}
            headerLegend={
              isFirstTrip ? t('your_seats_first_connection') : t('your_seats_second_connection')
            }
            key={trip.id}
            way={way}
            tripId={trip.id}
            layout={layouts[trip.id]}
            seats={seats[trip.id]}
            trip={trip}
            allowsSeatSelection={allowsSeatSelection}
            diagramType={diagramType}
            fetchingDetails={fetchingDetails}
            isPetFriendly={false}
            lockedSeats={tripLockedSeats}
            selectedSeats={selectedTripSeats}
            selectSeats={onSelectTripSeats(trip.id)}
            onFinishedSelection={onFinishSeatSelection(isFirstTrip)}
            sendNotification={sendNotification}
            isOpenTicket={isOpenTicket}
            isExchange={isExchange}
            passengers={isExchange && passengers}
            busCategories={busCategories}
            minPassengers={tripMinPassengers}
            updateSeat={updateSeat}
            isLoading={loading}
            canGoBack={canGoBack}
            tripPricing={tripPricing}
            resultsLayout={resultsLayout}
            onClose={onClose}
            hasSeats={hasSeats}
            clearError={clearError}
            fetchingDetailsError={fetchingDetailsError}
            bookingPassengerCount={bookingPassengerCount}
            seatPreferenceDiagramType={getSeatPreferenceDiagramType(
              trip.diagramType || trip.trip?.diagramType,
            )}
          />
        ) : null;
      })}
    </Spacing>
  );
};

MultiTripSeatSelector.propTypes = propTypes;

export default MultiTripSeatSelector;
